import { useQuery } from '@tanstack/react-query';
import { apiClient, type ApiResponse } from '@/api';
import { TENANT_API_URLS } from '@/api/constants/api.url';
import { readStoredAccess, setStoredAccess, type StoredAccess } from '@/shared/utils/stored-access';
import type { PermissionMap } from '@/shared/permissions';
import { useIsPlatformAdmin } from '@/shared/hooks/use-is-platform-admin';

/** Cache key for the signed-in member's permission set. Primed by login. */
export const accessCacheKeys = {
    all:     ['access'] as const,
    current: () => [...accessCacheKeys.all, 'current'] as const,
} as const;

/**
 * Read the signed-in member's delegated permissions from the query cache,
 * hydrated from localStorage on first call so a refresh doesn't blank out
 * gating. Login / workspace-switch prime the cache imperatively; when both cache
 * and storage are empty we fetch from the API and mirror it back.
 *
 * Returns:
 *   - `null`  → owner (full access), or not yet known
 *   - a map   → the managed member's feature-key → boolean grants
 *
 * Consumers should resolve owner-vs-managed via the session role
 * (see `useAccess`) — a managed member always carries a complete map.
 */
export function usePermissions(): PermissionMap | null {
    const stored = readStoredAccess();
    const isPlatformAdmin = useIsPlatformAdmin();
    const { data } = useQuery<StoredAccess>({
        queryKey: accessCacheKeys.current(),
        // Admin sidebar items carry no `feature`, so nothing consults this map
        // for a platform admin — skip the round trip.
        enabled: !isPlatformAdmin,
        queryFn:  async () => {
            try {
                const res = await apiClient.get<ApiResponse<{ permissions: PermissionMap | null }>>(
                    TENANT_API_URLS.permissions,
                );
                const access: StoredAccess = { permissions: res.data.data.permissions };
                setStoredAccess(access);
                return access;
            } catch {
                // API hiccup / not signed in — fall back to whatever's stored.
                return readStoredAccess() ?? { permissions: null };
            }
        },
        initialData: stored ?? undefined,
        staleTime:   Infinity,
        gcTime:      Infinity,
    });
    return data?.permissions ?? null;
}
